import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import AllWorkspacesQuery, {
  AllWorkspacesQueryResponse,
} from '@graphql/queries/AllWorkspacesQuery';
import OptimizationParentJobsQuery, {
  OptimizationParentJobsResponse,
} from '@graphql/queries/OptimizationParentJobsQuery';
import ParentReportCandidatesQuery from '@graphql/queries/ParentReportCandidatesQuery';

import { ParentReport, ReportKind } from '../model/types';

interface ReportJobsResponse {
  allReportJobs: {
    nodes: Array<Omit<ParentReport, 'workspaceId'> & { workspaceId: number; passedQa: boolean }>;
  };
}

export interface ParentReportRow extends ParentReport {
  passedQa: boolean;
  workspaceName?: string;
}

interface Options {
  kind: ReportKind | null;
  isSuperAdmin: boolean;
  workspaceId: number | null;
}

/**
 * Reports an optimization or mix & match can be built on. Mix & match only
 * makes sense for parents that already have a successful optimization run.
 */
const useParentReports = ({ kind, isSuperAdmin, workspaceId }: Options) => {
  const enabled = kind === 'optimization' || kind === 'mix_and_match';
  const scope = isSuperAdmin ? {} : { workspaceID: workspaceId };

  const jobs = useQuery<ReportJobsResponse>(ParentReportCandidatesQuery, {
    skip: !enabled,
    variables: { orderBy: 'STARTED_AT_DESC', ...scope },
  });
  const optimizedParents = useQuery<OptimizationParentJobsResponse>(
    OptimizationParentJobsQuery,
    {
      skip: kind !== 'mix_and_match',
      variables: { orderBy: 'STARTED_AT_DESC', ...scope },
    },
  );
  const workspaces = useQuery<AllWorkspacesQueryResponse>(AllWorkspacesQuery, {
    skip: !enabled,
  });

  const reports = useMemo<ParentReportRow[]>(() => {
    const names = new Map(
      (workspaces.data?.allProducers.nodes ?? []).map((w) => [w.id, w.name]),
    );
    const linked =
      kind === 'mix_and_match'
        ? new Set(
            (optimizedParents.data?.allReportJobs.nodes ?? []).map((n) => n.parentJobId),
          )
        : null;
    return (jobs.data?.allReportJobs.nodes ?? [])
      .filter((r) => !!r.jobId?.trim() && (!linked || linked.has(r.jobId)))
      .map((r) => ({
        reportId: r.reportId,
        jobId: r.jobId,
        startedAt: r.startedAt,
        clientName: r.clientName ?? '',
        projectName: r.projectName ?? '',
        targetGroupName: r.targetGroupName ?? '',
        workspaceId: r.workspaceId,
        passedQa: !!r.passedQa,
        workspaceName: names.get(r.workspaceId),
      }));
  }, [jobs.data, optimizedParents.data, workspaces.data, kind]);

  return {
    reports,
    loading: jobs.loading || optimizedParents.loading,
    error: jobs.error ?? optimizedParents.error,
  };
};

export default useParentReports;
